import React, {
  useCallback, useEffect, useState, useMemo,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Divider,
  Drawer,
  Row,
  Col,
  Select,
} from 'antd';
import HoverHelp from '../../common/HoverHelp';

import DrawerSubmitFooter from '../../common/containers/DrawerSubmitFooter';
import OnTraccrTextInput from '../../common/inputs/OnTraccrTextInput';
import DivisionSelector from '../../common/inputs/DivisionSelector';

import CustomExportTable from '../../reports/Exports/CustomExportsTable';

import ExportsColumnHeader from '../Exports/ExportsColumnHeader';

import {
  CUSTOM_EXPORT_PROP_TYPE,
  getExportFieldType,
} from '../Exports/exports.constants';

import {
  createCustomExport,
  updateCustomExport,
} from '../../reports/state/reports.actions';
import { getBoardCardTemplates, getBoards } from '../../boards/state/boards.actions';
import UserAssignmentSelector from '../../forms/FormWorkflows/selectors/UserAssignmentSelector';
import useCustomExportPermissions from '../../reports/Exports/useCustomExportPermissions';

const DATE_FIELD_TYPES = new Set(['dateTime']);

function BoardExportsDrawer({
  selectedExport,
  visible,
  onClose,
  type,
}) {
  const dispatch = useDispatch();
  const boardCardTemplates = useSelector((state) => state.boards.cardTemplates);
  const boards = useSelector((state) => state.boards.boards);

  const {
    selectedPermissions,
    permanentUserIds,
    buildAccessPayload,
    hydratePermissionsFromExport,
    clearPermissions,
    setSelectedPermissions,
  } = useCustomExportPermissions(selectedExport);

  const [columns, setColumns] = useState(selectedExport?.columns ?? []);
  const [title, setTitle] = useState(selectedExport?.title ?? null);
  const [divisionId, setDivisionId] = useState(selectedExport?.divisionId ?? null);
  const [boardId, setBoardId] = useState(selectedExport?.boardId ?? null);
  const [dateFilterFieldId, setDateFilterFieldId] = useState(
    selectedExport?.dateFilterFieldId ?? 'lastUpdated',
  );
  const [loading, setLoading] = useState(false);

  const ourBoardCardTemplate = useMemo(() => {
    const ourBoard = boards?.[boardId];
    if (!ourBoard) return null;
    return boardCardTemplates?.[ourBoard.cardTypeId];
  }, [boardCardTemplates, boards, boardId]);

  const boardOptions = useMemo(() => (
    Object.values(boards)
      .filter((board) => board.divisions?.includes?.(divisionId))
      .map((board) => ({
        label: board.title,
        value: board.id,
      }))
  ), [boards, divisionId]);

  const onSubmit = useCallback(async () => {
    if (!columns || columns.length === 0) {
      onClose();
      return;
    }
    setLoading(true);
    const access = buildAccessPayload();
    const payload = {
      title,
      columns,
      divisionId,
      boardId,
      boardCardTemplateId: boards?.[boardId]?.cardTemplateId,
      dateFilterFieldId,
      approvedOnly: false, // Board cards don't have approval workflows
      summarizeDays: false, // Board cards don't need time-based summarization
      summarizeProject: false, // Board cards don't need project summarization
      generateBatchNumber: false, // Board cards don't need batch numbers
      addLineNumbers: false, // Board cards don't need line numbers
      access,
    };
    if (!selectedExport) {
      payload.type = type;
    }
    const action = selectedExport
      ? updateCustomExport(selectedExport.id, payload)
      : createCustomExport(payload);
    if (await dispatch(action)) {
      onClose();
    }
    setLoading(false);
  }, [
    dispatch,
    selectedExport,
    columns,
    title,
    divisionId,
    boardId,
    dateFilterFieldId,
    buildAccessPayload,
    onClose,
    type,
  ]);

  const onTitleChanged = useCallback((e) => {
    const { target: { value } = {} } = e;
    setTitle(value);
  }, []);

  const onDivisionChanged = useCallback((newDivisionId) => {
    if (newDivisionId === divisionId) return;
    setDivisionId(newDivisionId);
    const {
      divisions: existingBoardDivisions = [],
    } = boards?.[boardId] ?? {};
    if (!existingBoardDivisions?.includes?.(newDivisionId)) {
      setColumns(columns.filter((col) => !col?.field?.startsWith?.('field')));
      setBoardId();
    }
  }, [divisionId, boards, boardId, columns]);

  const onDateFilterChanged = useCallback((value) => {
    setDateFilterFieldId(value);
  }, []);

  // Date field options for board cards
  const dateFieldOptions = useMemo(() => {
    const baseOptions = [
      { value: 'createdAt', label: 'Created On' },
      { value: 'lastUpdated', label: 'Last Updated' },
    ];

    if (!ourBoardCardTemplate) return baseOptions;

    ourBoardCardTemplate.fields?.forEach((section) => {
      section.fields?.forEach((field) => {
        if (!field) return;
        if (DATE_FIELD_TYPES.has(getExportFieldType(field))) {
          baseOptions.push({
            value: field.id,
            label: field.configProps?.title,
          });
        }
      });
    });

    return baseOptions;
  }, [ourBoardCardTemplate]);

  const previewConfig = useMemo(() => {
    const validCols = columns.filter((col) => col.title && (col.field || col.fields?.length));
    if (validCols.length === 0) return null;
    return {
      id: 'preview',
      title: title && title.length > 0 ? title : 'Export Preview',
      columns: validCols,
      divisionId,
      boardId,
      type,
      dateFilterFieldId,
    };
  }, [
    type,
    title,
    columns,
    divisionId,
    boardId,
    dateFilterFieldId,
  ]);

  useEffect(() => {
    if (selectedExport) {
      setTitle(selectedExport.title);
      setColumns(selectedExport.columns ?? []);
      setDivisionId(selectedExport.divisionId);
      setBoardId(selectedExport.boardId);
      setDateFilterFieldId(selectedExport.dateFilterFieldId ?? 'lastUpdated');
      hydratePermissionsFromExport();
    }
  }, [selectedExport]);

  useEffect(() => {
    if (!visible) {
      setColumns([]);
      setTitle();
      setDivisionId();
      setBoardId();
      setDateFilterFieldId('lastUpdated');
      clearPermissions();
    }
  }, [visible]);

  // Load board card templates for custom fields
  useEffect(() => {
    if (visible) {
      dispatch(getBoardCardTemplates());
      dispatch(getBoards());
    }
  }, [visible, dispatch]);

  return (
    <Drawer
      title={`${selectedExport ? 'Edit' : 'Add'} Board Card Export`}
      onClose={onClose}
      visible={visible}
      width="90%"
    >
      <Row align="middle" gutter={10}>
        <Col span={8}>
          <OnTraccrTextInput
            placeholder="Enter Export Title"
            onChange={onTitleChanged}
            value={title}
          />
        </Col>
        <Col>
          <DivisionSelector
            divisionId={divisionId}
            onChange={onDivisionChanged}
            allowClear={false}
            style={{ width: 250 }}
          />
        </Col>
        <Col>
          <Select
            placeholder="Select a board"
            value={boardId}
            onChange={setBoardId}
            style={{ width: 250 }}
            showSearch
            optionFilterProp="label"
            allowClear
            options={boardOptions}
          />
        </Col>
      </Row>

      <Row gutter={10} style={{ marginBottom: 16 }}>
        <Col span={24}>
          <UserAssignmentSelector
            divisions={divisionId ? [divisionId] : []}
            onChange={setSelectedPermissions}
            selected={selectedPermissions}
            mode="multiple"
            showPositions
            type="users"
            text="Select View Permissions:"
            disabled={!divisionId}
            permanentUserIds={permanentUserIds}
            allowClear={false}
          />
        </Col>
      </Row>

      {/* Date Filter Options for Board Cards */}
      <Row align="middle" gutter={10} style={{ marginTop: 10, marginBottom: 20 }}>
        <Col>
          <Select
            style={{ width: 240 }}
            value={dateFilterFieldId}
            onChange={onDateFilterChanged}
            options={dateFieldOptions}
            placeholder="Date Filter"
          />
        </Col>
        <Col>
          <HoverHelp
            content={(
              <div style={{ maxWidth: 260 }}>
                Select which date field to use for filtering board card exports.<br />
                "Created On" shows cards created within the selected time period.<br />
                "Last Updated" shows cards modified within the selected time period.
              </div>
            )}
          />
        </Col>
      </Row>

      <ExportsColumnHeader
        columns={columns}
        onColumnsChanged={setColumns}
        divisionId={divisionId}
        hasEclipse={false}
        type={type}
        boardCardTemplateId={ourBoardCardTemplate?.id}
      />
      <Divider />
      {previewConfig
        && (
          <CustomExportTable
            selectedExport={previewConfig}
            scroll="calc(100vh - 600px)"
          />
        )}
      <DrawerSubmitFooter
        onClose={onClose}
        onSubmit={onSubmit}
        canSubmit={!!title && !!divisionId && columns.length > 0}
        loading={loading}
      />
    </Drawer>
  );
}

BoardExportsDrawer.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedExport: CUSTOM_EXPORT_PROP_TYPE,
  type: PropTypes.string.isRequired,
};

BoardExportsDrawer.defaultProps = {
  selectedExport: null,
};

export default BoardExportsDrawer;
